import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '@theme';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button, Card } from '@/components/common';
import {
  ApplicationTimeline,
  getDefaultTimeline,
  StatusBadge,
  ScoreDisplay,
} from '@/components/applications';
import {
  FiArrowLeft,
  FiDownload,
  FiFileText,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiChevronLeft,
  FiChevronRight,
  FiUser,
  FiUsers,
  FiHome,
  FiBriefcase,
  FiDollarSign,
  FiTrendingUp,
  FiShield,
  FiUserCheck,
} from 'react-icons/fi';
import { Application, Candidate } from '@/models';
import { ApplicationServices } from '@/services/ApplicationServices';
import { CandidateServices } from '@/services/CandidateServices';

// Import des sections communes
import { PersonalInfoSection } from '@/components/application-forms/common/PersonalInfoSection';
import { CoupleSection } from '@/components/application-forms/common/CoupleSection';
import { HousingPreferencesSection } from '@/components/application-forms/common/HousingPreferencesSection';
import { DeclarationSection } from '@/components/application-forms/common/DeclarationSection';
import { FinancialComplementarySection } from '@/components/application-forms/common/FinancialComplementarySection';

// Import des sections SALARIÉ
import {
  EmploymentInfoSection,
  IncomeSection,
  FinancialSituationSection,
  GuaranteesSection,
} from '@/components/application-forms/employee';

// Import des sections ENTREPRENEUR
import {
  BusinessInfoSection,
  BusinessFinancialsSection,
  ClientsSuppliersSection,
  EntrepreneurGuaranteesSection,
} from '@/components/application-forms/entrepreneur';

// Import des sections SECTEUR INFORMEL
import {
  InformalActivitySection,
  InformalIncomeSection,
  InformalClientsSection,
  InformalSavingsSection,
  InformalExpensesSection,
  InformalReferencesSection,
  InformalProofsSection,
} from '@/components/application-forms/informal';

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.xl};
`;

const BackButton = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.text}60;
  cursor: pointer;
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.md};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.surface};
    color: ${theme.colors.accent};
  }

  svg {
    font-size: 24px;
  }
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.sizes['3xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
`;

const PageSubtitle = styled.p`
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.text}80;
  margin: 0;
`;


const FormContentArea = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.sm};
`;

const SectionDivider = styled.div`
  height: 1px;
  background: ${theme.colors.surface};
  margin: ${theme.spacing.xl} 0;
`;

// Navigation styles (même style qu'ApplicationFormMain.tsx)
const Container = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr 350px;
  gap: ${theme.spacing.xl};
  min-height: calc(100vh - 200px);

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  height: fit-content;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  box-shadow: ${theme.shadows.sm};
  position: sticky;
  top: ${theme.spacing.xl};
`;

const SidebarTitle = styled.h3`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.lg} 0;
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.surface};
`;

const CategoryBadge = styled.div<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${props => props.$color}20;
  color: ${props => props.$color};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${theme.typography.weights.semibold};
  margin-bottom: ${theme.spacing.lg};
`;

const CategoryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

const CategoryItem = styled.button<{ $active: boolean; $completed?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md};
  background: ${props =>
    props.$active ? theme.colors.primary + '15' :
      props.$completed ? theme.colors.success + '10' :
        'transparent'};
  border: none;
  border-left: 3px solid ${props =>
    props.$active ? theme.colors.primary :
      props.$completed ? theme.colors.success :
        'transparent'};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  text-align: left;
  width: 100%;

  &:hover {
    background: ${props =>
    props.$active ? theme.colors.primary + '20' :
      theme.colors.surface};
  }

  svg {
    font-size: 18px;
    color: ${props =>
    props.$active ? theme.colors.primary :
      props.$completed ? theme.colors.success :
        theme.colors.text + '60'};
  }
`;

const CategoryName = styled.div<{ $active: boolean }>`
  flex: 1;
  font-size: ${theme.typography.sizes.sm};
  font-weight: ${props => props.$active ? theme.typography.weights.semibold : theme.typography.weights.normal};
  color: ${props => props.$active ? theme.colors.primary : theme.colors.text};
`;

const FormHeader = styled.div`
  margin-bottom: ${theme.spacing.xl};
  padding-bottom: ${theme.spacing.lg};
  border-bottom: 2px solid ${theme.colors.surface};
`;

const FormTitle = styled.h2`
  font-size: ${theme.typography.sizes['2xl']};
  font-weight: ${theme.typography.weights.bold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};

  svg {
    font-size: 28px;
    color: ${theme.colors.primary};
  }
`;

const FormDescription = styled.p`
  font-size: ${theme.typography.sizes.base};
  color: ${theme.colors.text}80;
  margin: 0;
`;

const RightSidebar = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  height: fit-content;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  box-shadow: ${theme.shadows.sm};
  position: sticky;
  top: ${theme.spacing.xl};
`;

interface Section {
  id: number;
  name: string;
  icon: React.ReactNode;
  completed: boolean;
}

// Fonctions pour générer les sections selon le profil
const getEmployeeSections = (): Section[] => [
  { id: 1, name: 'Informations personnelles', icon: <FiUser />, completed: true },
  { id: 2, name: 'Candidature en couple', icon: <FiUsers />, completed: true },
  { id: 3, name: 'Situation professionnelle', icon: <FiBriefcase />, completed: true },
  { id: 4, name: 'Revenus du foyer', icon: <FiDollarSign />, completed: true },
  { id: 5, name: 'Situation financière', icon: <FiTrendingUp />, completed: true },
  { id: 6, name: 'Garanties', icon: <FiShield />, completed: true },
  { id: 7, name: 'Documents complémentaires', icon: <FiDollarSign />, completed: true },
  { id: 8, name: 'Préférences logement', icon: <FiHome />, completed: true },
  { id: 9, name: 'Déclarations', icon: <FiCheckCircle />, completed: true },
];

const getEntrepreneurSections = (): Section[] => [
  { id: 1, name: 'Informations personnelles', icon: <FiUser />, completed: true },
  { id: 2, name: 'Candidature en couple', icon: <FiUsers />, completed: true },
  { id: 3, name: 'Informations entreprise', icon: <FiBriefcase />, completed: true },
  { id: 4, name: 'Finances entreprise', icon: <FiDollarSign />, completed: true },
  { id: 5, name: 'Clients et fournisseurs', icon: <FiUsers />, completed: true },
  { id: 6, name: 'Garanties et références', icon: <FiShield />, completed: true },
  { id: 7, name: 'Documents complémentaires', icon: <FiDollarSign />, completed: true },
  { id: 8, name: 'Préférences logement', icon: <FiHome />, completed: true },
  { id: 9, name: 'Déclarations', icon: <FiCheckCircle />, completed: true },
];

const getInformalSections = (): Section[] => [
  { id: 1, name: 'Informations personnelles', icon: <FiUser />, completed: true },
  { id: 2, name: 'Candidature en couple', icon: <FiUsers />, completed: true },
  { id: 3, name: 'Votre activité', icon: <FiBriefcase />, completed: true },
  { id: 4, name: 'Vos revenus', icon: <FiDollarSign />, completed: true },
  { id: 5, name: 'Vos clients', icon: <FiUsers />, completed: true },
  { id: 6, name: 'Épargne et tontines', icon: <FiTrendingUp />, completed: true },
  { id: 7, name: 'Vos charges', icon: <FiDollarSign />, completed: true },
  { id: 8, name: 'Références', icon: <FiShield />, completed: true },
  { id: 9, name: 'Preuves d\'activité', icon: <FiFileText />, completed: true },
  { id: 10, name: 'Documents complémentaires', icon: <FiDollarSign />, completed: true },
  { id: 11, name: 'Préférences logement', icon: <FiHome />, completed: true },
  { id: 12, name: 'Déclarations', icon: <FiCheckCircle />, completed: true },
];

export const ApplicationDetails: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [application, setApplication] = useState<Application | null>(null);
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [currentSection, setCurrentSection] = useState(1);

  // Charger les données de l'application
  useEffect(() => {
    const loadApplicationData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!id) {
          setError('ID de candidature manquant');
          return;
        }

        // Charger l'application
        const appResponse = await ApplicationServices.get(Number(id));
        const appResult = appResponse as {
          success: boolean;
          data?: Application;
          message?: string;
        };

        if (!appResult.success || !appResult.data) {
          setError(appResult.message || 'Candidature non trouvée');
          return;
        }

        setApplication(appResult.data);

        // Charger les données du candidat
        if (appResult.data.candidate) {
          const candidateResponse = await CandidateServices.get(
            typeof appResult.data.candidate === 'number'
              ? appResult.data.candidate
              : appResult.data.candidate.id!
          );
          const candidateResult = candidateResponse as {
            success: boolean;
            data?: Candidate;
          };

          if (candidateResult.success && candidateResult.data) {
            setCandidate(candidateResult.data);
          }
        }
      } catch (err: any) {
        console.error('Error loading application:', err);
        setError(err.message || 'Erreur lors du chargement de la candidature');
      } finally {
        setLoading(false);
      }
    };

    loadApplicationData();
  }, [id]);

  // Vérifier si le score doit être affiché
  // Afficher le score uniquement si le statut est 'scored' ou après
  const shouldShowScore = () => {
    if (!application || !application.score) return false;

    const scoredStatuses = [
      'scored',
      'in_commission',
      'approved',
      'rejected',
      'ready_for_scoring'
    ];

    return scoredStatuses.includes(application.status || '');
  };

  // Obtenir les sections selon le profile_type
  const getSections = (): Section[] => {
    if (!candidate) return [];

    const profileType = candidate.profile_type;

    if (profileType === 'employee') return getEmployeeSections();
    if (profileType === 'entrepreneur') return getEntrepreneurSections();
    if (profileType === 'informal') return getInformalSections();

    return [];
  };

  const sections = getSections();
  const currentSectionData = sections.find(s => s.id === currentSection);

  const getCategoryColor = () => {
    if (!candidate) return theme.colors.primary;

    switch (candidate.profile_type) {
      case 'employee': return theme.colors.primary;
      case 'entrepreneur': return theme.colors.accent;
      case 'informal': return theme.colors.info;
      default: return theme.colors.primary;
    }
  };

  const getCategoryLabel = () => {
    if (!candidate) return '';

    switch (candidate.profile_type) {
      case 'employee': return 'SALARIÉ';
      case 'entrepreneur': return 'ENTREPRENEUR';
      case 'informal': return 'SECTEUR INFORMEL';
      default: return '';
    }
  };

  // Fonction pour créer une timeline dynamique basée sur les vraies données
  const getDynamicTimeline = (): any[] => {
    if (!application) return [];

    const formatDate = (dateString: string | undefined | null) => {
      if (!dateString) return undefined;
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
      } catch {
        return undefined;
      }
    };

    const currentStatus = application.status || 'draft';

    // Map des statuts avec leurs informations
    const statusMap: Record<string, { title: string; description: string; icon: React.ReactNode }> = {
      'draft': {
        title: 'Brouillon',
        description: 'Votre candidature est en cours de préparation',
        icon: <FiFileText />
      },
      'submitted': {
        title: 'Candidature soumise',
        description: 'Votre candidature a été enregistrée avec succès et est en attente de traitement',
        icon: <FiFileText />
      },
      'verification': {
        title: 'Vérification des documents',
        description: 'Vos documents sont en cours de vérification par nos équipes',
        icon: <FiUserCheck />
      },
      'pre_selected': {
        title: 'Présélection',
        description: 'Votre dossier a été présélectionné pour l\'étape suivante',
        icon: <FiCheckCircle />
      },
      'validated': {
        title: 'Dossier validé',
        description: 'Votre dossier a été validé et est prêt pour le scoring',
        icon: <FiUserCheck />
      },
      'ready_for_scoring': {
        title: 'Prêt pour scoring',
        description: 'Votre dossier est prêt pour l\'évaluation automatique',
        icon: <FiClock />
      },
      'scored': {
        title: 'Scoring effectué',
        description: `Votre dossier a été évalué. Score: ${application.score || 'N/A'}/100`,
        icon: <FiCheckCircle />
      },
      'in_commission': {
        title: 'En commission',
        description: 'Votre candidature est en cours d\'examen par la commission d\'attribution',
        icon: <FiUsers />
      },
      'approved': {
        title: 'Candidature approuvée',
        description: 'Félicitations ! Votre candidature a été approuvée par la commission',
        icon: <FiHome />
      },
      'rejected': {
        title: 'Candidature rejetée',
        description: 'Votre candidature n\'a malheureusement pas été retenue. Vous pouvez consulter les détails pour plus d\'informations.',
        icon: <FiXCircle />
      }
    };

    // Ordre des statuts dans le processus
    const statusOrder = [
      'draft',
      'submitted',
      'verification',
      'validated',
      'pre_selected',
      'ready_for_scoring',
      'scored',
      'in_commission',
      'approved'
    ];

    // Trouver l'index du statut actuel
    const currentIndex = statusOrder.indexOf(currentStatus);

    // Construire les événements de la timeline
    const events = statusOrder.map((status, index) => {
      const info = statusMap[status];

      // Déterminer le statut de l'événement
      let eventStatus: 'completed' | 'current' | 'pending' | 'rejected';
      if (currentStatus === 'rejected') {
        eventStatus = index <= 1 ? 'completed' : 'pending';
      } else if (index < currentIndex) {
        eventStatus = 'completed';
      } else if (index === currentIndex) {
        eventStatus = 'current';
      } else {
        eventStatus = 'pending';
      }

      return {
        id: status,
        status: eventStatus,
        title: info.title,
        description: info.description,
        date: index <= currentIndex ? formatDate(application.updated_at || application.created_at) : undefined,
        icon: info.icon
      };
    });

    // Si la candidature est rejetée, ajouter l'événement de rejet
    if (currentStatus === 'rejected') {
      const rejectedInfo = statusMap['rejected'];
      events.push({
        id: 'rejected',
        status: 'current' as 'current' | 'completed' | 'pending',
        title: rejectedInfo.title,
        description: rejectedInfo.description,
        date: formatDate(application.updated_at),
        icon: rejectedInfo.icon
      });
    }

    // Filtrer pour ne garder que les événements pertinents
    // Si en commission ou après, garder tous les événements
    // Sinon, garder uniquement les événements jusqu'au statut actuel + 2 suivants
    if (currentIndex >= statusOrder.indexOf('in_commission')) {
      return events;
    } else {
      return events.slice(0, currentIndex + 3);
    }
  };

  // Fonction pour rendre la section actuelle selon le profile_type
  const renderCurrentSection = () => {
    if (!candidate) return null;

    const profileType = candidate.profile_type;

    // SECTIONS SPÉCIFIQUES AU PROFIL SALARIÉ
    if (profileType === 'employee') {
      switch (currentSection) {
        case 1:
          return <PersonalInfoSection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 2:
          return <CoupleSection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 3:
          return <EmploymentInfoSection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 4:
          return <IncomeSection candidateData={candidate as any} spouseData={candidate as any} onCandidateChange={() => {}} onSpouseChange={() => {}} hasCoupleData={candidate.candidature_couple || false} readonly={true} />;
        case 5:
          return <FinancialSituationSection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 6:
          return <GuaranteesSection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 7:
          return <FinancialComplementarySection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 8:
          return <HousingPreferencesSection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 9:
          return <DeclarationSection data={candidate as any} onChange={() => {}} readonly={true} />;
        default:
          return null;
      }
    }

    // SECTIONS SPÉCIFIQUES AU PROFIL ENTREPRENEUR
    if (profileType === 'entrepreneur') {
      switch (currentSection) {
        case 1:
          return <PersonalInfoSection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 2:
          return <CoupleSection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 3:
          return <BusinessInfoSection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 4:
          return <BusinessFinancialsSection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 5:
          return <ClientsSuppliersSection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 6:
          return <EntrepreneurGuaranteesSection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 7:
          return <FinancialComplementarySection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 8:
          return <HousingPreferencesSection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 9:
          return <DeclarationSection data={candidate as any} onChange={() => {}} readonly={true} />;
        default:
          return null;
      }
    }

    // SECTIONS SPÉCIFIQUES AU PROFIL SECTEUR INFORMEL
    if (profileType === 'informal') {
      switch (currentSection) {
        case 1:
          return <PersonalInfoSection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 2:
          return <CoupleSection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 3:
          return <InformalActivitySection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 4:
          return <InformalIncomeSection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 5:
          return <InformalClientsSection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 6:
          return <InformalSavingsSection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 7:
          return <InformalExpensesSection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 8:
          return <InformalReferencesSection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 9:
          return <InformalProofsSection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 10:
          return <FinancialComplementarySection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 11:
          return <HousingPreferencesSection data={candidate as any} onChange={() => {}} readonly={true} />;
        case 12:
          return <DeclarationSection data={candidate as any} onChange={() => {}} readonly={true} />;
        default:
          return null;
      }
    }

    return null;
  };

  // Loading state
  if (loading) {
    return (
      <DashboardLayout>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          background: 'white',
          borderRadius: theme.borderRadius.lg,
          boxShadow: theme.shadows.sm
        }}>
          <div style={{ textAlign: 'center' }}>
            <p style={{ fontSize: theme.typography.sizes.lg, color: theme.colors.text + '60' }}>
              Chargement de la candidature...
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Error state
  if (error || !application || !candidate) {
    return (
      <DashboardLayout>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          background: 'white',
          borderRadius: theme.borderRadius.lg,
          boxShadow: theme.shadows.sm,
          padding: theme.spacing.xl
        }}>
          <p style={{ fontSize: theme.typography.sizes.lg, color: theme.colors.error, marginBottom: theme.spacing.md }}>
            {error || 'Candidature non trouvée'}
          </p>
          <Button onClick={() => navigate('/applications')}>
            <FiArrowLeft />
            Retour aux candidatures
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageHeader>
        <BackButton onClick={() => navigate('/applications')}>
          <FiArrowLeft />
        </BackButton>
        <HeaderContent>
          <PageTitle>Candidature #{application.id}</PageTitle>
          <PageSubtitle>
            {(typeof application.housing === 'object' && application.housing?.reference) || 'Logement'} - {candidate.profile_type || 'N/A'}
          </PageSubtitle>
        </HeaderContent>
        <StatusBadge
          status={application.status as any}
          size="lg"
          showIcon={true}
        />
      </PageHeader>

      {/* Layout avec navigation gauche, formulaire centre, et timeline droite */}
      <Container>
        {/* Sidebar gauche - Navigation */}
        <Sidebar>
          <SidebarTitle>Navigation</SidebarTitle>

          <CategoryBadge $color={getCategoryColor()}>
            {candidate.profile_type === 'employee' && <FiBriefcase />}
            {candidate.profile_type === 'entrepreneur' && <FiTrendingUp />}
            {candidate.profile_type === 'informal' && <FiUsers />}
            {getCategoryLabel()}
          </CategoryBadge>

          <CategoryList>
            {sections.map(section => (
              <CategoryItem
                key={section.id}
                $active={section.id === currentSection}
                $completed={section.completed}
                onClick={() => {
                  setCurrentSection(section.id);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              >
                {section.icon}
                <CategoryName $active={section.id === currentSection}>
                  {section.name}
                </CategoryName>
              </CategoryItem>
            ))}
          </CategoryList>
        </Sidebar>

        {/* Contenu principal - Formulaire */}
        <FormContentArea>
          <FormHeader>
            <FormTitle>
              {currentSectionData?.icon}
              {currentSectionData?.name}
            </FormTitle>
            <FormDescription>
              Section {currentSection} sur {sections.length}
            </FormDescription>
          </FormHeader>

          {/* Afficher la section actuelle */}
          {renderCurrentSection()}
        </FormContentArea>

        {/* Sidebar droite - Score et Timeline */}
        <RightSidebar>
          {/* Score - affiché seulement si le candidat est au stade scoring ou après */}
          {shouldShowScore() && (
            <div style={{ marginBottom: theme.spacing.lg }}>
              <ScoreDisplay score={application.score!} variant="card" showLabel={true} />
            </div>
          )}

          {/* Timeline dynamique */}
          <ApplicationTimeline
            events={getDynamicTimeline()}
            currentStatus={application.status || 'draft'}
          />
        </RightSidebar>
      </Container>
    </DashboardLayout>
  );
};
